import React, { useEffect, useRef, useState } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import TileLayer from "ol/layer/WebGLTile";
import GeoTIFF from "ol/source/GeoTIFF";
import View from "ol/View";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { GeoJSON } from "ol/format";
import Control from "ol/control/Control";
import { getFilePreSignedUrl, getHarvestDates } from "../../../utils/api";
import { blobDate, blobSitePrefix } from "../../../utils/demoSiteBlob";
import { LoadingIcon } from "../../../components";
import { Fill, Stroke, Style } from "ol/style";
import { DatePicker, message, Slider, Switch } from "antd";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../consts";
import dayjs from "dayjs";

const dummyJson = {
  type: "FeatureCollection",
  features: [],
};

function addSuffixToFilename(url, suffix) {
  const dotIndex = url.lastIndexOf(".");
  if (dotIndex === -1) return url + "_" + suffix;
  const base = url.substring(0, dotIndex);
  const extension = url.substring(dotIndex);
  return base + "_" + suffix + extension;
}

function createCompassControl() {
  const element = document.createElement("div");
  element.className = "heatmap-compass ol-unselectable ol-control";

  element.innerHTML = `
    <div class="compass-rose" style="
      position: relative;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: rgba(255,255,255,0.8);
      border: 1px solid #ccc;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: Arial, sans-serif;
    ">
      <div class="north" style="
        position: absolute;
        top: 3px;
        font-weight: bold;
        font-size: 12px;
      ">N</div>
      <div class="east" style="
        position: absolute;
        right: 3px;
        font-weight: bold;
        font-size: 12px;
      ">E</div>
      <div class="south" style="
        position: absolute;
        bottom: 3px;
        font-weight: bold;
        font-size: 12px;
      ">S</div>
      <div class="west" style="
        position: absolute;
        left: 3px;
        font-weight: bold;
        font-size: 12px;
      ">W</div>
    </div>
  `;

  return new Control({ element: element });
}

const HeatMapView = ({
  currentOrg,
  currentSite,
  siteMetadata,
  currentCrop,
  currentGreenhouse,
  currentPlot,
  isFullscreen = false,
  fullscreenAction = null,
  // dateList,
}) => {
  // Demo/clone sites read another site's blobs, possibly with shifted dates
  const cloudSitePrefix = blobSitePrefix(siteMetadata, currentSite);
  const toBlobDate = (d) => blobDate(siteMetadata, currentGreenhouse?.name, d);

  const [messageApi, contextHolder] = message.useMessage();
  const [cogFileUrl, setCogFileUrl] = useState(null);
  const [heatMapJson, setHeatMapJson] = useState(dummyJson);
  const [loadingMetadata, setLoadingMetadata] = useState(true);
  const [loadingMapData, setLoadingMapData] = useState(true);
  const [error, setError] = useState(null);

  const [dateList, setDateList] = useState([]);

  const [showHeatMap, setShowHeatMap] = useState(true);
  const [currentDate, setCurrentDate] = useState(null);
  const [validDates, setValidDates] = useState([]);

  const [gridSizes, setGridSizes] = useState([]);
  const [baseGridSizeValue, setBaseGridSizeValue] = useState(null);
  const [baseGridSizeUnit, setBaseGridSizeUnit] = useState(null);
  const [selectedGridSize, setSelectedGridSize] = useState(null);

  // Refs for the map and layers
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const vectorLayerRef = useRef(null);

  // Tooltip state
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState("");
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const resetMap = () => {
    // 1) If there's an old map, remove target so OL can clean up
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setTarget(null);
    }
    // 2) Clear out refs
    mapInstanceRef.current = null;
    tileLayerRef.current = null;
    vectorLayerRef.current = null;
    setTooltipOpen(false);
    setTooltipTitle("");
    setMousePos({ x: 0, y: 0 });
  };

  const getPreSignedUrl = async (url) => {
    try {
      const preSignedUrl = await getFilePreSignedUrl(
        currentOrg,
        currentSite.id,
        currentCrop.name,
        url,
        siteMetadata?.CLOUD_STORAGE_NAME || "azure",
        true,
        true,
      );
      return preSignedUrl?.data?.data?.url;
    } catch (error) {
      setError("Error Fetching data !!!");
      return null;
    }
  };

  const convertCaretToSuperscript = (text) => {
    // Map for superscript numbers
    const superscriptMap = {
      0: "⁰",
      1: "¹",
      2: "²",
      3: "³",
      4: "⁴",
      5: "⁵",
      6: "⁶",
      7: "⁷",
      8: "⁸",
      9: "⁹",
    };

    // Replace ^ followed by numbers with corresponding superscripts
    return text.replace(/\^(\d+)/g, (_, numbers) =>
      numbers
        .split("")
        .map((digit) => superscriptMap[digit])
        .join(""),
    );
  };

  const fetchHeatMapJson = async (heatMapURL) => {
    setLoadingMapData(true);
    try {
      const preSignedUrl = await getPreSignedUrl(heatMapURL);
      if (!preSignedUrl) {
        setError("No file heatmap json found !!!");
        return null;
      }
      const response = await fetch(preSignedUrl);
      if (!response.ok) {
        setError("Failed to fetch heatmap JSON data");
        setLoadingMapData(false);
        return null;
      }
      const jsonData = await response.json();
      setHeatMapJson(jsonData);
    } catch (error) {
      setError("Failed to fetch heatmap JSON data");
    } finally {
      // We'll set loadingMapData to false after vector layer updates
    }
  };

  const fetchCogUrl = async (cogURL, baseHeatMapJson) => {
    const url = await getPreSignedUrl(cogURL);
    if (url) {
      setCogFileUrl(url);
      const updatedHeatMapJson = addSuffixToFilename(
        baseHeatMapJson,
        selectedGridSize,
      );
      setError(null);
      fetchHeatMapJson(updatedHeatMapJson);
    } else {
      setError("No NDVI found !!!");
      setLoadingMapData(false);
      return null;
    }
  };

  const interpolateColor = (color1, color2, factor) => {
    const result = color1.slice();
    for (let i = 0; i < 3; i++) {
      result[i] = Math.round(result[i] + factor * (color2[i] - color1[i]));
    }
    return result;
  };

  const colorStops = [
    { threshold: 0.0, color: [139, 69, 19] }, // Dark brown
    { threshold: 0.1, color: [160, 82, 45] }, // Sienna-like brown
    { threshold: 0.2, color: [205, 133, 63] }, // Peru brown
    { threshold: 0.3, color: [222, 184, 135] }, // Burlywood
    { threshold: 0.4, color: [238, 232, 170] }, // Pale Goldenrod, transitioning toward lighter
    { threshold: 0.5, color: [189, 236, 182] }, // Light greenish hue
    { threshold: 0.6, color: [156, 220, 144] }, // Light green
    { threshold: 0.7, color: [118, 205, 118] }, // Medium green
    { threshold: 0.8, color: [85, 179, 85] }, // More vibrant green
    { threshold: 0.9, color: [34, 139, 34] }, // Forest Green (high density)
    { threshold: 1.0, color: [0, 100, 0] }, // Dark Green (highest density)
  ];

  const getColorByValueWithGradient = (normalizedValue) => {
    for (let i = 0; i < colorStops.length - 1; i++) {
      const lower = colorStops[i];
      const upper = colorStops[i + 1];
      if (
        normalizedValue >= lower.threshold &&
        normalizedValue <= upper.threshold
      ) {
        const range = upper.threshold - lower.threshold;
        const factor =
          range === 0 ? 0 : (normalizedValue - lower.threshold) / range;
        const rgb = interpolateColor(lower.color, upper.color, factor);
        return `rgba(${rgb.join(",")}, 0.6)`;
      }
    }
    return `rgba(255, 0, 0, 0.6)`;
  };

  function formatGridSize(size, baseValue, unit) {
    const multiplier = parseFloat(size.replace("x", ""));
    const calculatedValue = multiplier * baseValue;
    return `${size} (${calculatedValue} ${unit})`;
  }

  // 1. Load grid metadata
  useEffect(() => {
    if (currentSite && siteMetadata) {
      const gridSizeArray = siteMetadata?.["HEATMAP_NDVI_GRID_SIZES"];
      const gridBaseSizeValue = siteMetadata?.["HEATMAP_NDVI_BASE_GRID_SIZE"];
      const gridBaseSizeUnit =
        siteMetadata?.["HEATMAP_NDVI_BASE_GRID_SIZE_DEFAULT_UNIT"];

      if (Array.isArray(gridSizeArray) && gridSizeArray.length > 0) {
        setGridSizes(gridSizeArray);
        setBaseGridSizeValue(gridBaseSizeValue);
        setBaseGridSizeUnit(gridBaseSizeUnit);
        setSelectedGridSize(gridSizeArray[0]); // default to first
        setLoadingMetadata(false);
      } else {
        setError("Grid Size metadata not found");
        setLoadingMetadata(false);
      }
    }
  }, [currentSite, siteMetadata]);

  // 2. On selectedGridSize changes, fetch JSON (only once fetch COG if needed)
  useEffect(() => {
    if (
      !currentSite ||
      !currentCrop ||
      !currentGreenhouse ||
      !currentPlot ||
      !currentDate
    )
      return;
    setError(null);
    setLoadingMapData(true);
    resetMap();

    const cogURL = `${cloudSitePrefix}/${currentCrop.name}/${toBlobDate(currentDate)}/${currentGreenhouse.name}/${currentPlot.name}/heatmap/ndvi.tif`;
    const baseHeatMapJson = `${cloudSitePrefix}/${currentCrop.name}/${toBlobDate(currentDate)}/${currentGreenhouse.name}/${currentPlot.name}/heatmap/grid.json`;
    fetchCogUrl(cogURL, baseHeatMapJson);
  }, [currentSite, siteMetadata, currentCrop, currentGreenhouse, currentPlot, currentDate]);

  useEffect(() => {
    if (
      !dateList?.length ||
      !currentSite ||
      !currentCrop ||
      !currentGreenhouse ||
      !currentPlot
    ) {
      setValidDates([]);
      return;
    }

    let isMounted = true;

    const checkAllDates = async () => {
      try {
        const promises = dateList.map(async (d) => {
          const cogURL = `${cloudSitePrefix}/${currentCrop.name}/${toBlobDate(d)}/${currentGreenhouse.name}/${currentPlot.name}/heatmap/ndvi.tif`;

          const url = await getPreSignedUrl(cogURL);
          return url ? d : null;
        });

        const results = await Promise.all(promises);

        const foundDates = results.filter(Boolean);
        if (isMounted) {
          setValidDates(foundDates);
        }
      } catch (err) {
        console.error("Error checking dates:", err);
        if (isMounted) {
          setValidDates([]);
        }
      }
    };

    checkAllDates();

    return () => {
      isMounted = false;
    };
  }, [dateList, currentSite, siteMetadata, currentCrop, currentGreenhouse, currentPlot]);

  useEffect(() => {
    if (
      !selectedGridSize ||
      !currentSite ||
      !currentCrop ||
      !currentGreenhouse ||
      !currentPlot ||
      !currentDate
    )
      return;

    const baseHeatMapJson = `${cloudSitePrefix}/${currentCrop.name}/${toBlobDate(currentDate)}/${currentGreenhouse.name}/${currentPlot.name}/heatmap/grid.json`;

    if (baseHeatMapJson) {
      const updatedHeatMapJson = addSuffixToFilename(
        baseHeatMapJson,
        selectedGridSize,
      );
      fetchHeatMapJson(updatedHeatMapJson);
    }
  }, [
    selectedGridSize,
    currentSite,
    siteMetadata,
    currentCrop,
    currentGreenhouse,
    currentPlot,
    currentDate,
  ]);

  // 3. Create the map ONLY once, after we have cogFileUrl & heatMapJson
  //    (But do NOT re-run if showHeatMap changes!)
  useEffect(() => {
    if (!cogFileUrl || !mapRef.current) return;
    // resetMap();
    // Create the tile layer from the COG
    const source = new GeoTIFF({ sources: [{ url: cogFileUrl }] });
    tileLayerRef.current = new TileLayer({ source });

    // Create the vector layer (NDVI grid)
    vectorLayerRef.current = new VectorLayer({
      source: new VectorSource(),
      style: (feature) => {
        const value = feature.get("normalizedValue") || 0;
        const fillColor = getColorByValueWithGradient(value);
        return new Style({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: "#fff", width: 1 }),
        });
      },
    });

    // Create the map instance
    const map = new Map({
      target: mapRef.current,
      layers: [tileLayerRef.current, vectorLayerRef.current],
      view: new View({ center: [0, 0], zoom: 2 }),
    });
    mapInstanceRef.current = map;

    // Compass control
    const compassControl = createCompassControl();
    map.addControl(compassControl);

    // Center/zoom the map from COG metadata
    source.getView().then((viewOptions) => {
      if (viewOptions) {
        map.setView(new View(viewOptions));
      }
      setTimeout(() => {
        map.updateSize();
      }, 0);
    });

    // Hide tooltip on mouse leave
    const mapContainer = map.getTargetElement();
    mapContainer?.addEventListener("mouseleave", () => {
      setTooltipOpen(false);
    });
  }, [cogFileUrl]);

  // 4. Update the vector layer data whenever heatMapJson changes (but NOT showHeatMap).
  useEffect(() => {
    if (!mapInstanceRef.current || !vectorLayerRef.current) return;
    if (!heatMapJson.features?.length) return;

    const values = heatMapJson.features.map((f) => f.properties.value);
    const maxVal = Math.max(...values);
    const minVal = Math.min(...values);
    const range = maxVal - minVal || 1;

    const normalize = (value) => (value - minVal) / range;

    const normalizedData = {
      ...heatMapJson,
      features: heatMapJson.features.map((feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          normalizedValue: normalize(feature.properties.value),
        },
      })),
    };

    // Re-bind pointermove listener
    const map = mapInstanceRef.current;

    const handlePointerMove = (evt) => {
      if (evt.dragging) {
        setTooltipOpen(false);
        return;
      }
      const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f);
      if (feature) {
        const rawVal = Number(feature.get("value"));
        const rawValUnit = feature.get("unit") || baseGridSizeUnit || "";
        if (!isNaN(rawVal)) {
          const rawValue = rawVal.toFixed(2);
          const percentage = ((rawVal / maxVal) * 100).toFixed(2);
          setTooltipTitle(
            `Value: ${rawValue} ${convertCaretToSuperscript(rawValUnit)}<br/>Relative Percentage: ${percentage}%`,
          );
          setTooltipOpen(true);
          setMousePos({ x: evt.pixel[0] + 10, y: evt.pixel[1] + 10 });
        } else {
          setTooltipOpen(false);
        }
      } else {
        setTooltipOpen(false);
      }
    };

    // Remove existing listener before adding a new one
    map.un("pointermove", handlePointerMove);
    map.on("pointermove", handlePointerMove);

    // Update vector layer source
    const newFeatures = new GeoJSON().readFeatures(normalizedData, {
      featureProjection: "EPSG:4326",
    });
    const vectorSrc = new VectorSource({ features: newFeatures });
    vectorLayerRef.current.setSource(vectorSrc);

    // Fit view to new data
    const extent = vectorSrc.getExtent();
    if (extent && extent[0] !== Infinity) {
      map.getView().fit(extent, {
        padding: isFullscreen ? [20, 20, 20, 20] : [50, 50, 50, 50],
      });
    }

    // stop loading
    setLoadingMapData(false);

    return () => {
      map.un("pointermove", handlePointerMove);
    };
  }, [heatMapJson, isFullscreen, cogFileUrl]);

  // 5. Toggle NDVI grid visibility ONLY (do not reload map or data)
  useEffect(() => {
    if (vectorLayerRef.current) {
      vectorLayerRef.current.setVisible(showHeatMap);
    }
  }, [showHeatMap]);

  const handleDateChange = (date, dateString) => {
    if (dateString) {
      setCurrentDate(date.format(BACKEND_DATE_FORMAT));
      setLoadingMapData(true);
      setError(null);
    }
  };

  const fetchHarvestDates = async (orgId, siteId, cropId) => {
    try {
      const res = await getHarvestDates(orgId, siteId, cropId);
      const dates = res.data?.data?.map((dateObj) => dateObj.date);
      setDateList(dates);
    } catch (err) {
      console.log({ err });
    }
  };

  useEffect(() => {
    if (currentSite && currentCrop && currentOrg) {
      fetchHarvestDates(currentOrg, currentSite.id, currentCrop.id);
    }
  }, [currentSite, currentCrop, currentOrg]);

  useEffect(() => {
    if (validDates && validDates.length > 0) {
      if (!validDates.includes(currentDate)) {
        setError(null);
        const sortedDates = [...validDates].sort(
          (a, b) => new Date(b) - new Date(a),
        );
        setCurrentDate(sortedDates[0]);
      }
    }
  }, [validDates]);

  const disabledDate = (current) => {
    const dayjsDates = validDates.map((date) => dayjs(date));
    return (
      current &&
      !dayjsDates.reduce((isEnabled, date) => {
        if (isEnabled) {
          return true;
        } else {
          return date.isSame(current, "day");
        }
      }, false)
    );
  };

  //   const disabledDate = (current) => {
  //   if (!current) return true;
  //   const formatted = dayjs(current).format("YYYY-MM-DD");
  //   return !validDates.includes(formatted);
  // };

  // Handle toggling heatmap
  const handleToggleHeatMap = (checked) => {
    setShowHeatMap(checked);
    // vectorLayerRef.current?.setVisible(checked);
    // Optionally do it here, but we do it in the effect above
  };

  // Slider logic
  const parsedSizes = gridSizes.map((gs) => parseFloat(gs));
  const isConsecutive = parsedSizes.every(
    (val, i, arr) => i === 0 || val === arr[i - 1] + 1,
  );

  const currentSliderValue = selectedGridSize
    ? parseFloat(selectedGridSize)
    : parsedSizes[0];

  const handleSliderChange = (val) => {
    const idx = parsedSizes.indexOf(val);
    if (idx !== -1) {
      setSelectedGridSize(gridSizes[idx]);
    }
  };

  let step;
  let marks = {};
  if (isConsecutive) {
    step = 1;
  } else {
    step = null;
    for (const ps of parsedSizes) {
      marks[ps] = ps.toString();
    }
  }

  return (
    <div
      className={`heat-map w-full rounded-md relative ${
        isFullscreen
          ? "mb-0 flex h-full min-h-0 flex-col p-2 sm:p-1"
          : "mb-5 p-4 shadow-md sm:p-1 hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101"
      }`}
    >
      <h2 className={`font-bold ${isFullscreen ? "mb-2 text-lg" : "mb-3 text-lg"}`}>
        Heat Map
      </h2>
      {contextHolder}

      <div
        className={`z-50 ${
          isFullscreen
            ? "mb-3 flex w-full flex-wrap items-center justify-end gap-3"
            : "absolute right-2 top-[2rem] mb-4 flex flex-row items-center space-y-0 space-x-4 sm:flex-col sm:items-start sm:space-y-2 sm:space-x-0"
        }`}
      >
        {!error && (
          <>
            {/* Grid Size Slider only if showHeatMap=true */}
            <div className="flex items-center space-x-2 justify-end">
              <span className="font-bold whitespace-nowrap mr-2">
                Grid Size:
              </span>
              <Slider
                value={currentSliderValue}
                min={parsedSizes[0]}
                max={parsedSizes[parsedSizes.length - 1]}
                step={step}
                marks={marks}
                onChange={handleSliderChange}
                tooltip={{
                  formatter: (val) => {
                    const idx = parsedSizes.indexOf(val);
                    return idx !== -1
                      ? formatGridSize(
                          gridSizes[idx],
                          baseGridSizeValue,
                          baseGridSizeUnit,
                        )
                      : val;
                  },
                }}
                className="flex-1 min-w-[150px] max-w-[300px]"
                disabled={loadingMetadata || loadingMapData || !showHeatMap}
              />
            </div>

            <div className="flex items-center space-x-2 justify-end">
              <span className="font-bold">Show Heatmap:</span>
              <Switch
                checked={showHeatMap}
                onChange={handleToggleHeatMap}
                checkedChildren="On"
                unCheckedChildren="Off"
                disabled={loadingMetadata || loadingMapData}
              />
            </div>
          </>
        )}
        {currentDate && (
          <div className="flex items-center justify-end gap-2">
            <DatePicker
              value={currentDate ? dayjs(currentDate) : null}
              className={`max-w-32 min-w-[150px] ${
                isFullscreen ? "" : "sm:mt-4 sm:mb-6"
              }`}
              disabledDate={disabledDate}
              onChange={handleDateChange}
              format={UI_DATE_FORMAT}
              placeholder="Select Date"
              disabled={loadingMapData}
            />
            {fullscreenAction}
          </div>
        )}
        {!currentDate && fullscreenAction && (
          <div className="flex items-center justify-end">{fullscreenAction}</div>
        )}
      </div>

      <div
        className={isFullscreen ? "relative min-h-0 flex-1" : "mt-6"}
        style={{ position: "relative", minHeight: isFullscreen ? "0" : "70vh" }}
      >
        {error && (
          <div className="text-center text-red-500 font-bold mt-10">
            {error ? error : "Error fetching the NDVI data."}
          </div>
        )}

        {loadingMetadata && !error && (
          <div
            className="flex justify-center items-center"
            style={{ height: isFullscreen ? "100%" : "70vh" }}
          >
            <LoadingIcon />
          </div>
        )}

        {!loadingMetadata && !error && (
          <div
            ref={mapRef}
            className={`map w-full rounded-md ${
              isFullscreen ? "h-full min-h-0" : "sm:mt-32"
            }`}
            style={{ height: isFullscreen ? "100%" : "70vh", position: "relative" }}
          >
            {loadingMapData && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 z-50">
                <LoadingIcon />
              </div>
            )}

            {tooltipOpen && (
              <div
                style={{
                  position: "absolute",
                  left: mousePos.x,
                  top: mousePos.y,
                  pointerEvents: "none",
                  zIndex: 9999,
                  background: "rgba(0, 0, 0, 0.7)",
                  color: "#fff",
                  padding: "5px",
                  borderRadius: "3px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                }}
                dangerouslySetInnerHTML={{ __html: tooltipTitle }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { HeatMapView };
